/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import { useStore, getSmoothStepPath, getBezierPath } from '@xyflow/react';
import { edgeStyles } from './NodeStyles';
import { getEdgeParams } from '../utils';

// Modal styles for edge editing
const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  modal: {
    backgroundColor: '#272727',
    color: '#e2e8f0',
    borderRadius: '8px',
    padding: '20px',
    width: '500px',
    maxWidth: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    position: 'relative'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    borderBottom: '1px solid #444',
    paddingBottom: '8px'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    color: '#e2e8f0',
    cursor: 'pointer'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  label: {
    fontWeight: 'bold',
    marginBottom: '4px'
  },
  input: {
    backgroundColor: '#333',
    color: '#e2e8f0',
    border: '1px solid #444',
    borderRadius: '4px',
    padding: '8px',
    width: '100%'
  },
  textarea: {
    backgroundColor: '#333',
    color: '#e2e8f0',
    border: '1px solid #444',
    borderRadius: '4px',
    padding: '8px',
    minHeight: '100px',
    width: '100%',
    fontFamily: 'monospace',
    resize: 'vertical'
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '16px'
  },
  button: {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  cancelButton: {
    backgroundColor: '#4b5563',
    color: '#e2e8f0'
  },
  saveButton: {
    backgroundColor: '#8b5cf6',
    color: '#ffffff'
  }
};

// Edit Edge Modal component
export const EditEdgeModal = ({ isOpen, onClose, edgeData, onSave }) => {
  const [condition, setCondition] = useState(edgeData?.conditionExpression || '');
  const [error, setError] = useState('');

  useEffect(() => {
    if (edgeData?.conditionExpression) {
      setCondition(edgeData.conditionExpression);
    }
  }, [edgeData]);

  const handleSave = () => {
    // Basic validation of the JavaScript expression
    try {
      // Test if the expression is valid JavaScript
      new Function('state', `return ${condition}`);
      setError('');
      onSave({
        ...edgeData,
        conditionExpression: condition,
        conditional: true
      });
    } catch (e) {
      setError('Invalid JavaScript expression');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: '#2d2d2d',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        display: isOpen ? 'block' : 'none',
        width: '500px',
        color: '#fff'
      }}
    >
      <h2 style={{ marginTop: 0, color: '#fff' }}>Configure Edge Condition</h2>
      <p style={{ color: '#a1a1aa', marginBottom: '20px' }}>
        Enter a JavaScript expression that evaluates to true when this edge should be taken. Use 'state' to access the
        current state of the graph.
      </p>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', color: '#fff' }}>Condition Expression:</label>
        <textarea
          value={condition}
          onChange={(e) => setCondition(e.target.value)}
          style={{
            width: '100%',
            height: '100px',
            padding: '10px',
            borderRadius: '4px',
            backgroundColor: '#1a1a1a',
            color: '#fff',
            border: '1px solid #444',
            fontFamily: 'monospace'
          }}
          placeholder="e.g., state.messages.at(-1).content.includes('summarize')"
        />
      </div>

      {error && <div style={{ color: '#ef4444', marginBottom: '20px' }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button
          onClick={onClose}
          style={{
            padding: '8px 16px',
            backgroundColor: '#444',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          style={{
            padding: '8px 16px',
            backgroundColor: '#a78bfa',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
};

// Custom edge component with improved styling
export const CustomEdge = ({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
  markerEnd,
  markerStart
}) => {
  const [edgePath, edgeCenterX, edgeCenterY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    curvature: data?.isHorizontalConnection ? 0.25 : 0.5
  });

  // Determine edge color based on source and target node types

  let edgeColor;
  if (data?.sourceType === 'agent' && data?.targetType === 'tool') {
    edgeColor = edgeStyles.colors.agent;
  } else if (data?.sourceType === 'tool') {
    edgeColor = edgeStyles.colors.tool;
  } else if (data?.sourceType === 'start') {
    edgeColor = edgeStyles.colors.start;
  } else if (data?.sourceType === 'end') {
    edgeColor = edgeStyles.colors.end;
  } else if (data?.sourceType === 'supervisor') {
    edgeColor = edgeStyles.colors.supervisor;
  } else {
    edgeColor = edgeStyles.colors.agent;
  }

  // Determine if this is a conditional edge
  const isConditional = data?.conditional === true;

  // Combine styles
  const finalStyle = {
    ...edgeStyles.base,
    ...style,
    stroke: style?.stroke || edgeColor, // fallback to edgeColor if not set
    strokeDasharray: style?.strokeDasharray || (isConditional ? edgeStyles.dashed.strokeDasharray : 'none'),
    strokeWidth: style?.strokeWidth || (isConditional ? 2.5 : 2)
  };

  const interactionStyle = {
    ...edgeStyles.base,
    ...style,
    stroke: 'transparent',
    strokeDasharray: 'none',
    strokeWidth: 30
  };

  return (
    <>
      <path
        id={id}
        style={finalStyle}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
        markerStart={markerStart}
        onClick={(event) => {
          if (data?.onEdgeClick) data.onEdgeClick(event, { id, data });
        }}
      />
      <path style={interactionStyle} d={edgePath} fill="none" className="edge-hover-path" />
      {data?.label && (
        <text
          x={edgeCenterX}
          y={edgeCenterY}
          textAnchor="middle"
          alignmentBaseline="middle"
          style={{
            fill: edgeColor,
            fontSize: '12px',
            pointerEvents: 'none',
            userSelect: 'none',
            background: 'white',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {data.label}
        </text>
      )}
    </>
  );
};
